import { useEffect, useMemo, useState } from "react"
import { BarChart3, MessageSquareHeart } from "lucide-react"
import { ToastMessage, useToast } from "@stayframe/ui"
import { apiClient, formatApiError, type AdminFeedbackSummary } from "@/lib/api"
import { PAGE_DISPLAY_NAMES } from "@/components/feedback"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

const PERIOD_OPTIONS = [
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 365 days", value: 365 },
] as const

const PAGE_ORDER = ["overview", "performance", "reviews", "listings"] as const

function formatTimeAgo(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Recently"
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return "just now"
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}y ago`
}

function npsToneClass(nps: number): string {
  if (nps >= 50) return "text-success"
  if (nps >= 0) return "text-warning"
  return "text-destructive"
}

function segmentToneClass(segment: string): string {
  if (segment === "promoter") return "bg-success text-success"
  if (segment === "passive") return "bg-warning text-warning"
  return "bg-destructive text-destructive"
}

function firstAnswerSnippet(summaryItem: AdminFeedbackSummary["recent"][number]): string {
  const firstAnswer = summaryItem.answers[0]
  if (!firstAnswer) return "No follow-up captured"
  if (firstAnswer.selected_option === "Other") {
    return firstAnswer.other_text?.trim() || "Other"
  }
  return firstAnswer.selected_option
}

export function OpsFeedbackCard() {
  const { toast } = useToast()
  const [days, setDays] = useState<number>(90)
  const [summary, setSummary] = useState<AdminFeedbackSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function loadFeedbackSummary() {
      setLoading(true)
      const response = await apiClient.getAdminFeedbackSummary(days)
      if (cancelled) return

      if (response.ok && response.data) {
        setSummary(response.data)
        setLoading(false)
        return
      }

      setSummary(null)
      setLoading(false)
      const error = formatApiError(response as unknown as Record<string, unknown>)
      toast(<ToastMessage message={error.message || "Failed to load page feedback."} requestId={error.requestId} />, "error")
    }

    void loadFeedbackSummary()

    return () => {
      cancelled = true
    }
  }, [days, toast])

  const totalResponses = useMemo(() => {
    if (!summary) return 0
    return Object.values(summary.pages).reduce((total, page) => total + (page?.total || 0), 0)
  }, [summary])

  const pageCards = useMemo(() => {
    if (!summary) return []
    return PAGE_ORDER.map((pageSlug) => {
      const page = summary.pages[pageSlug] || {
        nps: 0,
        total: 0,
        detractors: 0,
        passives: 0,
        promoters: 0,
      }
      return {
        slug: pageSlug,
        name: PAGE_DISPLAY_NAMES[pageSlug],
        ...page,
      }
    })
  }, [summary])

  return (
    <Card className="border-border/60 bg-card/95 shadow-sm">
      <CardHeader className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-semibold">Page Feedback</CardTitle>
        </div>
        <Select value={String(days)} onValueChange={(value) => setDays(Number(value) || 90)}>
          <SelectTrigger className="h-9 w-[160px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading ? (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }, (_, index) => (
                <div key={index} className="rounded-lg border border-border/50 bg-muted/20 p-3">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="mt-3 h-8 w-16" />
                  <Skeleton className="mt-2 h-3 w-24" />
                  <Skeleton className="mt-4 h-2 w-full" />
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        ) : !summary || totalResponses === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 px-6 py-10 text-center">
            <MessageSquareHeart className="h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium text-foreground">No feedback collected yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Scores and follow-ups from Overview, Performance, Reviews, and Listing Intelligence will show up here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {pageCards.map((page) => {
                const total = page.total || 0
                const detractorPct = total ? (page.detractors / total) * 100 : 0
                const passivePct = total ? (page.passives / total) * 100 : 0
                const promoterPct = total ? (page.promoters / total) * 100 : 0

                return (
                  <div key={page.slug} className="rounded-lg border border-border/60 bg-muted/30 p-3 text-center">
                    <p className="text-xs font-medium text-muted-foreground">{page.name}</p>
                    <p className={cn("mt-2 text-2xl font-bold", npsToneClass(page.nps))}>
                      {page.nps >= 0 ? page.nps : `${page.nps}`}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {page.total} response{page.total === 1 ? "" : "s"}
                    </p>
                    <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-muted">
                      {total ? (
                        <>
                          <span className="bg-destructive/60" style={{ width: `${detractorPct}%` }} />
                          <span className="bg-warning/60" style={{ width: `${passivePct}%` }} />
                          <span className="bg-success/60" style={{ width: `${promoterPct}%` }} />
                        </>
                      ) : (
                        <span className="w-full bg-muted" />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Recent Feedback</h3>
                <span className="text-[11px] text-muted-foreground">Newest first</span>
              </div>
              <div className="max-h-[320px] overflow-y-auto rounded-xl border border-border/60 bg-background/40 px-4">
                {summary.recent.slice(0, 10).map((item) => (
                  <div key={item.id} className="flex items-start gap-3 border-b border-border/30 py-3 last:border-0">
                    <span className={cn("mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full", segmentToneClass(item.segment))} />
                    <div className={cn("w-5 text-sm font-semibold", item.segment === "promoter" ? "text-success" : item.segment === "passive" ? "text-warning" : "text-destructive")}>
                      {item.score}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          {PAGE_DISPLAY_NAMES[item.page_slug as keyof typeof PAGE_DISPLAY_NAMES] || item.page_slug}
                        </Badge>
                        <span className="max-w-[140px] truncate text-xs text-muted-foreground">
                          {item.user_email || "Unknown user"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">{formatTimeAgo(item.created_at)}</span>
                      </div>
                      <p className="mt-1 truncate text-xs italic text-muted-foreground">
                        "{firstAnswerSnippet(item)}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-border/60 bg-background/40 p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Top Issues (Detractors)
                </h3>
                <div className="space-y-2.5">
                  {summary.top_issues.length ? (
                    summary.top_issues.map((item) => (
                      <div key={`${item.page}-${item.option}`} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-foreground">{item.option}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {PAGE_DISPLAY_NAMES[item.page as keyof typeof PAGE_DISPLAY_NAMES] || item.page}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-[10px]">
                          {item.count}×
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No detractor issues captured in this window.</p>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-background/40 p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Top Praise (Promoters)
                </h3>
                <div className="space-y-2.5">
                  {summary.top_praise.length ? (
                    summary.top_praise.map((item) => (
                      <div key={`${item.page}-${item.option}`} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-foreground">{item.option}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {PAGE_DISPLAY_NAMES[item.page as keyof typeof PAGE_DISPLAY_NAMES] || item.page}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-[10px]">
                          {item.count}×
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No promoter praise captured in this window.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
